import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { Button } from 'reactstrap';
import { injectIntl, FormattedMessage } from 'react-intl';
import FontAwesome from 'react-fontawesome';

import { getConversations, mergeConversationsRequest } from '../../ConversationActions';
import { getLoggedUser } from '../../../User/UserActions';

import ConversationListItem from './ConversationListItem';
import ConversationAddCard from './ConversationAddCard';
import ConversationTypeSelector from '../ConversationTypeSelector';
import { displayErrors } from '../../../Error/ErrorActions';

class ConversationList extends PureComponent {
    constructor(props) {
        super(props);

        this.state = {
            isMergeMode: false,
            isLoading: false,
            conversationsToMerge: [],
            addPanelPhase: 'closed',
            addSlotHeight: 0,
            addPanelHeight: 0,
        };

        this.addSlotRef = React.createRef();
        this.addPanelRef = React.createRef();
        this.scrollRestoreY = null;
    }

    componentDidMount() {
        this.syncAddAreaHeight();
    }

    componentDidUpdate(prevProps, prevState) {
        const { addPanelPhase } = this.state;
        const prev = prevState.addPanelPhase;

        if(prev !== addPanelPhase) {
            this.syncAddAreaHeight();
        }

        if(prev === 'closed' && (addPanelPhase === 'exiting' || addPanelPhase === 'open')) {
            this.scrollToAddPanel();
            return;
        }

        if(['open', 'exiting'].includes(prev) && addPanelPhase === 'entering') {
            this.scrollToAddSlot();
        } else if(['open', 'exiting'].includes(prev) && addPanelPhase === 'closed') {
            this.scrollToAddSlot(true);
        }
    }

    getScrollMargin = () => 24;

    getScrollBehavior = () => (
        typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
    );

    scrollToAddPanel = () => {
        if(typeof window === 'undefined') {
            return;
        }
        window.requestAnimationFrame(() => {
            this.addPanelRef.current?.scrollIntoView({ behavior: this.getScrollBehavior(), block: 'start' });
        });
    };

    scrollToAddSlot = (afterClose = false) => {
        if(typeof window === 'undefined') {
            return;
        }
        const behavior = this.getScrollBehavior();
        const scrollMargin = this.getScrollMargin();

        const restoreScroll = () => {
            if(this.scrollRestoreY != null) {
                window.scrollTo({
                    top: Math.max(0, this.scrollRestoreY - scrollMargin),
                    behavior,
                });
                this.scrollRestoreY = null;
                return true;
            }
            return false;
        };

        if(restoreScroll()) {
            return;
        }

        // Fallback once the "+" slot has its height back (mobile collapse animation).
        if(!afterClose) {
            return;
        }

        window.requestAnimationFrame(() => {
            const slot = this.addSlotRef.current;
            if(!slot) {
                return;
            }
            const top = slot.getBoundingClientRect().top + window.scrollY - scrollMargin;
            window.scrollTo({ top: Math.max(0, top), behavior });
        });
    };

    captureAddSlotHeight = () => {
        const height = this.addSlotRef.current?.offsetHeight;
        if(height) {
            this.setState({ addSlotHeight: height });
        }
    };

    syncAddAreaHeight = () => {
        if(typeof window === 'undefined') {
            return;
        }

        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                const slotEl = this.addSlotRef.current;
                const panelEl = this.addPanelRef.current;
                const updates = {};

                if(slotEl?.offsetHeight) {
                    updates.addSlotHeight = slotEl.offsetHeight;
                }
                if(panelEl?.offsetHeight) {
                    updates.addPanelHeight = panelEl.offsetHeight;
                }

                if(Object.keys(updates).length) {
                    this.setState(prevState => ({
                        addSlotHeight: updates.addSlotHeight ?? prevState.addSlotHeight,
                        addPanelHeight: updates.addPanelHeight ?? prevState.addPanelHeight,
                    }));
                }
            });
        });
    };

    prefersReducedMotion = () => (
        typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );

    getAddAreaStyle() {
        const { addPanelPhase, addSlotHeight, addPanelHeight } = this.state;

        if(this.prefersReducedMotion()) {
            return undefined;
        }

        const closedHeight = addSlotHeight;
        const openHeight = addPanelHeight || addSlotHeight;
        let height;

        if(addPanelPhase === 'closed' || addPanelPhase === 'entering') {
            height = closedHeight;
        } else {
            height = openHeight;
        }

        return height ? { height } : undefined;
    }

    handleAddCardAnimationComplete = phase => {
        if(phase === 'exiting') {
            this.setState({ addPanelPhase: 'open' });
        } else if(phase === 'entering') {
            this.setState({ addPanelPhase: 'closed' });
        }
    };

    handleOpenAddPanel = () => {
        if(this.state.addPanelPhase !== 'closed') {
            return;
        }
        this.captureAddSlotHeight();
        if(typeof window !== 'undefined') {
            this.scrollRestoreY = window.scrollY;
        }
        if(typeof window !== 'undefined' && this.prefersReducedMotion()) {
            this.setState({ addPanelPhase: 'open' });
            return;
        }
        this.setState({ addPanelPhase: 'exiting' });
    };

    handleCloseAddPanel = () => {
        if(!['open', 'exiting'].includes(this.state.addPanelPhase)) {
            return;
        }
        if(typeof window !== 'undefined' && this.prefersReducedMotion()) {
            this.setState({ addPanelPhase: 'closed' });
            return;
        }
        this.setState({ addPanelPhase: 'entering' });
    };

    handleAddConversationsToMerge = (conversation, checked = true) => {
        const newConversationsToMerge = this.state.conversationsToMerge.filter(conversationId => conversationId !== conversation._id);
        this.setState({
            conversationsToMerge: checked ? [...newConversationsToMerge, conversation._id] : newConversationsToMerge,
        });
    };

    handleMerge = event => {
        this.setState({
            isLoading: true,
        });
        this.props.dispatch(mergeConversationsRequest(this.state.conversationsToMerge)).then(isOk => {
            this.setState({
                isLoading: false,
                isMergeMode: false,
            });
            isOk && displayErrors('success', this.props.intl.formatMessage({ id: 'conversationMergeRequestSuccess', defaultMessage: 'Conversations will be merged soon! You will receive an email when merge is finished.' }));
        });
    };

    render() {
        const { conversations, count } = this.props;
        const { addPanelPhase } = this.state;
        const isAddPanelVisible = addPanelPhase !== 'closed';
        const isAddPanelExiting = addPanelPhase === 'entering';
        if(conversations.length) {
            const hasMultipleConversations = conversations.length > 1;
            const { isMergeMode } = this.state;

            return (
                <div className="conversations-lux-wrap">
					{
						isMergeMode && (
							<div className="conversations-lux-listhead">
								<span className="conversations-lux-mergeinfo"><FormattedMessage id="conversationMergeInfo" defaultMessage="Select conversations to be merged and click on 'Merge' button below" /></span>
							</div>
						)
					}
                    <div className={classNames('conversations-lux-grid', isMergeMode && 'conversations-lux-grid--merge')}>
                        {conversations.filter(conversation => conversation._id).sort((convA, convB) => new Date(convB.dateStatus?.pending) - new Date(convA.dateStatus?.pending)).slice(0, count || conversations.length).map(conversation => (
                            <ConversationListItem
                                key={conversation._id}
                                conversation={conversation}
                                isMergeMode={isMergeMode}
                                isSelected={this.state.conversationsToMerge.includes(conversation._id)}
                                onToggleSelection={this.handleAddConversationsToMerge}
                            />
                        ))}
                        {!isMergeMode && (
                            <div
                                className={classNames(
                                    'conversations-lux-add-area',
                                    isAddPanelVisible && 'conversations-lux-add-area--expanded',
                                )}
                                style={this.getAddAreaStyle()}
                            >
                                {isAddPanelVisible && (
                                    <div
                                        ref={this.addPanelRef}
                                        className={classNames('conversations-lux-addpanel', isAddPanelExiting && 'conversations-lux-addpanel--exiting')}
                                    >
                                        <button
                                            type="button"
                                            className="conversations-lux-addpanel__close"
                                            onClick={this.handleCloseAddPanel}
                                            aria-label={this.props.intl.formatMessage({ id: 'cancel', defaultMessage: 'Cancel' })}
                                        >
                                            <span aria-hidden="true">×</span>
                                        </button>
                                        <ConversationTypeSelector />
                                    </div>
                                )}
                                <div
                                    ref={this.addSlotRef}
                                    className="conversations-lux-add-slot"
                                >
                                    <ConversationAddCard
                                        intl={this.props.intl}
                                        phase={addPanelPhase}
                                        onOpen={this.handleOpenAddPanel}
                                        onAnimationComplete={this.handleAddCardAnimationComplete}
                                    />
                                    <div className="conversations-lux-merge-hint">
                                        <span className="conversations-lux-merge-hint__eyebrow atelier-eyebrow">
                                            <FormattedMessage id="conversationMergeHintEyebrow" defaultMessage="Good to know" />
                                        </span>
                                        <p className="conversations-lux-merge-hint__text">
                                            {
                                                hasMultipleConversations
                                                    ? <FormattedMessage id="conversationMergeHintReady" defaultMessage="You can merge your conversations into a single chronological conversation." />
                                                    : <FormattedMessage id="conversationMergeHintSingle" defaultMessage="Several chat threads? Import each conversation with +, then merge them into one conversation." />
                                            }
                                        </p>
                                        {
                                            hasMultipleConversations && (
                                                <button
                                                    type="button"
                                                    className="conversations-lux-merge-hint__cta"
                                                    onClick={() => this.setState({ isMergeMode: true })}
                                                >
                                                    <FormattedMessage id="conversationMerge" defaultMessage="Merge conversations in one" />
                                                </button>
                                            )
                                        }
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    {isMergeMode && (
						<div className="conversations-lux-mergebar">
							<Button color="link" className="btn-atelier-ghost" onClick={() => this.setState({ isMergeMode: false })}><FormattedMessage id="cancel" defaultMessage="Cancel" /></Button>
							<button type="button" className="btn-atelier-primary" disabled={this.state.conversationsToMerge.length < 2 || this.state.isLoading} onClick={this.handleMerge}>{this.state.isLoading ? <FontAwesome name="spinner" spin /> : <FontAwesome name="check-circle" />} <FormattedMessage id="conversationMerge" defaultMessage="Merge conversations" /></button>
						</div>
					)}
                </div>
            );
        }
        return <ConversationTypeSelector />;
    }
}

function mapStateToProps(store, props) {
    const user = getLoggedUser(store);
    return {
        conversations: getConversations(store, user),
    };
}

ConversationList.propTypes = {
    dispatch: PropTypes.func.isRequired,
    intl: PropTypes.object.isRequired,
    conversations: PropTypes.arrayOf(PropTypes.object),
    count: PropTypes.number,
};

export default connect(mapStateToProps)(injectIntl(ConversationList));
